<template>
  <div
    class="tenancy-opening-hours"
  >
    <div
      v-if="openingHours.updatedBy && !isEditing"
      class="last-update__text row"
    >
      {{ lastUpdatedByOn }}
    </div>
    <div class="opening-hour-section">
      <label>{{ $t('tenancy.regularOpeningHours') }}</label>
      <div class="opening-hour-btn-group">
        <i
          v-if="!isEditing && canEdit"
          class="fa fa-pencil"
          @click="isEditing=true"
        />
        <button
          v-if="!isEditing && canEdit"
          class="btn-edit btn--withoutcolor"
          role="edit-button"
          @click="isEditing=true"
        >
          {{ $t('action.edit') }}
        </button>
        <button
          v-if="isEditing"
          role="cancel-button"
          class="btn-cancel btn--withoutcolor"
          @click="cancel"
        >
          {{ $t('action.cancel') }}
        </button>
        <button
          v-if="isEditing"
          role="save-button"
          class="btn-save btn--withoutcolor"
          @click="save"
        >
          {{ $t('action.save') }}
        </button>
      </div>
    </div>

    <div
      v-if="!isEditing"
      class="view-mode"
    >
      <ul
        v-if="(openingHours.regularWorkingHours || []).length"
        class="tenancy-working-hours-list"
      >
        <li
          v-for="(item, index) in openingHours.regularWorkingHours"
          :key="index"
        >
          <span>{{ item.workingDayFrom | customWorkingDayLabel }}</span>
          <span v-if="item.workingDayTo"> - </span>
          <span class="u-margin-right-small">{{ item.workingDayTo | customWorkingDayLabel }}</span>
          <span>{{ item.workingHourFrom | customWorkingHourLabel }}</span>
          <span v-if="item.workingHourTo"> - </span>
          <span>{{ item.workingHourTo | customWorkingHourLabel }}</span>
        </li>
      </ul>
      <p v-else>
        {{ $t('emptyValue') }}
      </p>

      <div
        class="tenancy-working-hours"
      >
        <label>{{ $t('tenancy.holidayOpeningHours') }}</label>
        <ul
          v-if="(openingHours.holidayWorkingHours || []).length"
          class="tenancy-working-hours-list"
        >
          <li
            v-for="(item, index) in openingHours.holidayWorkingHours"
            :key="index"
            class="holiday-hour-view"
          >
            <div class="working-desc-view-mode">
              <span>{{ item.workingDesc }}</span>
            </div>
            <div>
              <span>{{ formattedDate(item.workingDayFrom) }}</span>
              <span v-if="item.workingDayTo"> - </span>
              <span class="u-margin-right-small">{{ formattedDate(item.workingDayTo) }}</span>
              <span>{{ item.workingHourFrom | customWorkingHourLabel }}</span>
              <span v-if="item.workingHourTo"> - </span>
              <span>{{ item.workingHourTo | customWorkingHourLabel }}</span>
            </div>
          </li>
        </ul>
        <p v-else>
          {{ $t('emptyValue') }}
        </p>
      </div>
    </div>
    <div
      v-else
      class="edit-mode"
    >
      <WorkingHours
        :hideLabel="true"
        :workingHours="openingHours.regularWorkingHours || []"
        :updateValueByPath="updateTenancyPathField"
        :fieldPath="['regularWorkingHours']"
        :checkTimeValidate="false"
      />
      <label>{{ $t('tenancy.holidayOpeningHours') }}</label>
      <HolidayOpeningHours
        :hideLabel="true"
        :workingHours="openingHours.holidayWorkingHours || []"
        :updateValueByPath="updateTenancyPathField"
        :fieldPath="['holidayWorkingHours']"
      />
    </div>
  </div>
</template>

<script>

import mutationTypes from '@/store/mutation-types'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import dateUtil from '@/utils/dateUtil'
import WorkingHours from '@/controls/WorkingHours'
import filters from 'filters/filters'
import HolidayOpeningHours from '@/modules/ccd/tenancy/HolidayOpeningHours'
import { aclHelper, ccdPermsTypes } from '@/utils/aclHelper'
import { utils as formUtils } from '@/plugin/InputChange'
import { getTenancyDisplayName } from '@/modules/ccd/tenancy/utils'

export default {
  name: 'TenancyOpeningHours',
  components: {
    HolidayOpeningHours,
    WorkingHours,
  },
  props: {
  },
  data () {
    return {
      isEditing: false,
    }
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    openingHours () {
      return this.currentTenancy.openingHours || {}
    },
    canEdit () {
      const canEngineerEdit = aclHelper.getAccess(ccdPermsTypes.tenancy.manageMarketingContacts) || aclHelper.getAccess(ccdPermsTypes.tenancy.update)
      return this.isTenant || canEngineerEdit
    },
    lastUpdatedByOn () {
      const name = getTenancyDisplayName(this.currentTenancy, this.openingHours.user)
      return this.$t('lastUpdatedBy', [name, filters['formatDate'](this.openingHours.updatedAt, 'DD MMM YY HH:mm')])
    },
  },
  methods: {
    ...mapMutations('ccd', {
      updateTenancyPathField: mutationTypes.TENANCY_UPDATE_OPENING_HOURS,
    }),
    ...mapActions('ccd', [
      'updateOpeningHours',
      'getOpeningHours',
    ]),
    async save () {
      await this.updateOpeningHours({tenancyId: this.currentTenancy.id, request: this.currentTenancy.openingHours, isSilentUpdate: false})
      this.isEditing = false
      await this.getOpeningHours({tenancyId: this.currentTenancy.id})
      formUtils.resetDirty()
    },
    async cancel () {
      this.isEditing = false
      await this.getOpeningHours({tenancyId: this.currentTenancy.id})
      formUtils.resetDirty()
    },
    formattedDate (date) {
      return dateUtil.format(date, dateUtil.DD_MMM_YYYY)
    },
  },

}
</script>

<style lang="scss" scoped>
.tenancy-opening-hours {
  padding: 15px;
  padding-top: 5px;
  border-top: 1px solid #f1f1f1;
  .opening-hour-section {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
  }
  .last-update__text {
    padding-top: 10px;
    font-size: 12px;
  }
  .holiday-hour-view{
    display: flex;
    .working-desc-view-mode {
      width:30%;
    }
  }
}
</style>
